import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import {
  PROMPT_REPEAT_MIN_SECONDS,
  REWARD_ICONS,
  useSettings,
  type RewardIcon,
} from '../settings/SettingsContext';
import { canSpeak, speak, stopSpeaking } from './speak';
import { pausedMsSince, useTalkBoardOpen } from '../talk/talkOpen';
import { playCorrectPing } from './ping';
import { endSession, fetchRewardSummary, type RewardSummary } from '../rewards/rewards';
import FaceText from '../media/FaceText';

type RoundView = {
  roundId: string;
  programId: string;
  kind: string;
  /** Resolved from the program's promptMode against the supervisor's default. */
  promptType: 'AUDIO' | 'TEXT';
  promptText: string;
  tiles: string[];
  /** Resolved server-side from the program's override and the supervisor's default. */
  errorless?: boolean;
  /** The tile face that answers this prompt — not the prompt itself. */
  expected?: string;
  /** The first go of the day: no help, and it earns nothing. */
  probe?: boolean;
  /** Whether to offer the learner a way to stop. */
  breakOffered?: boolean;
  repeatSeconds?: number | null;
};

type AttemptResult = {
  correct: boolean;
  roundComplete: boolean;
  attempts: number;
  /** How much this answer earned, if the every-N rule came due on it. */
  rewarded: number;
};

type Props = {
  programId: string;
  learnerId?: string;
};

// Pause that lets the learner register the green tile + ping before anything moves.
const FEEDBACK_HOLD_MS = 700;

function gridClass(n: number): string {
  if (n <= 4) return 'cols-2';
  if (n <= 9) return 'cols-3';
  return 'cols-4';
}

export default function ReceptiveWords({ programId, learnerId }: Props) {
  const { token } = useAuth();
  const { settings } = useSettings();
  const [round, setRound] = useState<RoundView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [correctTile, setCorrectTile] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [collected, setCollected] = useState<RewardSummary | null>(null);
  /* Counted here as answers earn them, rather than asked for again after every trial. */
  const [earnedNow, setEarnedNow] = useState(0);
  const [fadingOut, setFadingOut] = useState(false);
  const playPromptRef = useRef<() => void>(() => {});
  const shownAtRef = useRef<number>(0);
  const promptsRef = useRef<number>(0);
  /* One sitting, named once. The server refuses a second reward for the same name, so a
     duplicate end — a remount, a double navigation — costs nothing. */
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const answeredRef = useRef(false);

  const startRound = useCallback(async () => {
    setError(null);
    setBusy(true);
    setCorrectTile(null);
    setCompleted(false);
    setOnBreak(false);
    stopSpeaking();
    try {
      const r = await api<RoundView>('/games/start', {
        method: 'POST',
        token,
        body: { programId, learnerId },
      });
      setRound(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start round');
    } finally {
      setBusy(false);
      // Once the new round is on screen (or we've shown an error), bring opacity back.
      // The CSS transition handles the visible fade-in.
      setFadingOut(false);
    }
  }, [programId, learnerId, token]);

  useEffect(() => { startRound(); }, [startRound]);

  useEffect(() => {
    if (!settings.features.rewardTotal) return;
    let cancelled = false;
    void fetchRewardSummary(learnerId ?? null).then((s) => {
      if (!cancelled) setCollected(s);
    });
    return () => { cancelled = true; };
  }, [learnerId, settings.features.rewardTotal]);

  // Speak the current target. Wrong taps just re-speak; no negative feedback ever.
  const playPrompt = useCallback(() => {
    if (!round) return;
    promptsRef.current += 1;
    if (round.promptType === 'AUDIO' && canSpeak()) {
      speak(round.promptText, {
        voiceName: settings.ttsVoice,
        rate: settings.ttsRate,
      });
    }
  }, [round, settings.ttsVoice, settings.ttsRate]);

  useEffect(() => { playPromptRef.current = playPrompt; }, [playPrompt]);

  // Auto-play once a new round arrives.
  useEffect(() => {
    if (!round) return;
    shownAtRef.current = performance.now();
    promptsRef.current = 0;
    const t = window.setTimeout(() => playPromptRef.current(), 350);
    return () => window.clearTimeout(t);
  }, [round]);

  // Periodic replay until the learner gets it right. Min 5s; configurable per-user.
  const talkOpen = useTalkBoardOpen();
  const talkWasOpen = useRef(talkOpen);
  useEffect(() => {
    if (talkWasOpen.current && !talkOpen && round && !completed) playPromptRef.current();
    talkWasOpen.current = talkOpen;
  }, [talkOpen]);

  useEffect(() => {
    if (!round || completed || talkOpen) return;
    const seconds = Math.max(PROMPT_REPEAT_MIN_SECONDS, round.repeatSeconds ?? settings.promptRepeatSeconds);
    const id = window.setInterval(() => playPromptRef.current(), seconds * 1000);
    return () => window.clearInterval(id);
  }, [round, completed, settings.promptRepeatSeconds, talkOpen]);

  // Correct-answer transition:
  //   1. Hold the green tile + confirmation ping for FEEDBACK_HOLD_MS so the learner
  //      registers what just happened.
  //   2. Begin the configured between-rounds animation (CSS handles it).
  //   3. Once the animation has completed, fetch and show the next round, which animates back in.
  useEffect(() => {
    if (!completed) return;
    const transitionMs = settings.roundTransitionType === 'NONE' ? 0 : settings.roundTransitionMs;
    const fadeStart = window.setTimeout(() => setFadingOut(true), FEEDBACK_HOLD_MS);
    const advance = window.setTimeout(() => { startRound(); }, FEEDBACK_HOLD_MS + transitionMs);
    return () => {
      window.clearTimeout(fadeStart);
      window.clearTimeout(advance);
    };
  }, [completed, startRound, settings.roundTransitionMs, settings.roundTransitionType]);

  // Stop any in-flight speech if we unmount mid-round.
  useEffect(() => () => stopSpeaking(), []);

  /* THE SITTING ENDS WHEN THE BOARD GOES AWAY, and only then. Best effort on purpose: a
     closed tab never reports it, and nothing is lost when it does not — the every-N rule
     works out what it owes from the trials themselves. */
  useEffect(() => {
    const session = sessionIdRef.current;
    return () => {
      void endSession(programId, session, answeredRef.current);
    };
  }, [programId]);

  async function pickTile(tile: string) {
    if (!round || completed) return;

    /*
     * ERRORLESS: A WRONG TILE DOES NOT RESPOND, AND IS NOT RECORDED.
     *
     * Handled here rather than server-side, and the distinction matters. If the
     * attempt were posted and refused, the round would count an error — and an
     * error recorded is exactly what errorless teaching exists to prevent,
     * because a mistake repeated gets learned alongside the target. So the tap
     * simply does nothing: no request, no attempt count, no prompt replay.
     *
     * The comparison is the same one the server makes on a real attempt, so a
     * learner cannot reach a different verdict by tapping.
     */
    /* COMPARED AGAINST THE ANSWER, NOT THE PROMPT. This used to read
       `tile !== round.promptText`, which was only ever right because content
       was a list of words where the two were the same string. The moment a
       prompt differs from its tile face — "something red" answered by an apple
       — every tile mismatches the prompt, so errorless would have swallowed
       every tap including the correct one and left the game unplayable with
       nothing on screen to explain it.

       Falls back to promptText so a word-matching program behaves exactly as
       before if it is talking to an older API. */
    const answer = (round.expected ?? round.promptText).trim().toLowerCase();
    if (round.errorless && tile.trim().toLowerCase() !== answer) {
      return;
    }

    try {
      const result = await api<AttemptResult>(`/games/rounds/${round.roundId}/attempt`, {
        method: 'POST',
        token,
        body: {
          tile,
          respondedMs: Math.round(performance.now() - shownAtRef.current - pausedMsSince(shownAtRef.current)),
          promptsPlayed: promptsRef.current,
        },
      });
      if (result.correct) {
        answeredRef.current = true;
        if (result.rewarded > 0) setEarnedNow((n) => n + result.rewarded);
        setCorrectTile(tile);
        setCompleted(true);
        if (settings.correctPingEnabled) playCorrectPing();
      } else {
        playPrompt();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record attempt');
    }
  }

  async function takeBreak() {
    if (!round) return;
    stopSpeaking();
    await api<{ recorded: boolean }>(`/games/rounds/${round.roundId}/break`, {
      method: 'POST',
      token,
    }).catch(() => undefined);
    setOnBreak(true);
  }

  const tilesGridClass = useMemo(() => gridClass(round?.tiles.length ?? 4), [round]);

  if (error) {
    return (
      <div className="card">
        <div className="error" role="alert">{error}</div>
      </div>
    );
  }
  if (busy && !round) return null;
  if (!round) return null;

  if (onBreak) {
    return (
      <div className="card">
        <h2>Break time</h2>
        <p className="field-hint">
          Asking to stop is written down as asking to stop. It is not a wrong answer and it
          does not count against anything.
        </p>
        <div className="toolbar">
          <button className="btn" onClick={() => { void startRound(); }}>
            Ready to carry on
          </button>
        </div>
      </div>
    );
  }

  const effectiveMs = settings.roundTransitionType === 'NONE' ? 0 : settings.roundTransitionMs;
  const transitionStyle = { '--round-transition-ms': `${effectiveMs}ms` } as React.CSSProperties;
  const typeClass = `round-${settings.roundTransitionType.toLowerCase()}`;

  return (
    <>
      {/* NOT ANNOUNCED. The board is deliberately quiet — no hover lift, no colour beyond the
          one that says "yes" — and a live region firing on every tap would cut against that for
          exactly the learners it is meant to serve. It is there to be looked at, not heard. */}
      {settings.features.rewardTotal && collected?.enabled && (
        <div className="board-total">
          <span aria-hidden="true">
            {REWARD_ICONS[collected.icon as RewardIcon] ?? REWARD_ICONS.star}
          </span>
          <strong>{collected.total + earnedNow}</strong>
          <span>{collected.name}</span>
        </div>
      )}
      {round.probe && (
        <div className="banner" role="status">
          First go today, so no help is given and nothing is earned. It shows what they can
          do on their own.
        </div>
      )}
      {round.promptType === 'TEXT' && (
        <div className="concept-prompt-text" aria-live="polite">{round.promptText}</div>
      )}
      <div
        className={`tile-grid ${tilesGridClass} ${typeClass}${fadingOut ? ' fading-out' : ''}`}
        style={transitionStyle}
        role="group"
        aria-label="Word tiles"
        aria-live="polite"
      >
      {round.tiles.map((tile, i) => {
        const isCorrect = completed && correctTile === tile;
        return (
          <button
            key={`${tile}-${i}`}
            className={`tile${isCorrect ? ' correct' : ''}`}
            onClick={() => pickTile(tile)}
            disabled={completed}
            aria-label={`Tile: ${tile}`}
          >
            <FaceText text={tile} />
          </button>
        );
      })}
      </div>
      {round.breakOffered && !completed && (
        <div className="toolbar break-bar">
          <button className="btn secondary" onClick={() => void takeBreak()}>
            I need a break
          </button>
        </div>
      )}
    </>
  );
}
